import { useBandSubscriptionContext, usePermissionsContext } from "Bands/contexts";
import { BAND_PATHS } from "Bands/router";

import { BillingStatus } from "Bands/types";
import { getDaysLeft, useBandRoute } from "Bands/utils";
import { FC, useCallback, useMemo } from "react";

import { copy } from "shared/assets";
import { Button, Badge, Message } from "shared/components";
import { Spacer } from "shared/layout";
import { BoldText } from "shared/styles";
import { IStatus } from "shared/types";

const messageCopy = copy.calendar.subscriptionMessage;

interface SubscriptionMessageProps {
  isCenter?: boolean;
}

export const SubscriptionMessage: FC<SubscriptionMessageProps> = ({ isCenter = true }) => {
  const { currentSubscription, isTrial, isActiveTrial, isActiveSubscription } =
    useBandSubscriptionContext();
  const { bandPermissions } = usePermissionsContext();
  const { pushRoute } = useBandRoute();

  const daysLeft = useMemo<number | null>(
    () => (currentSubscription ? getDaysLeft(currentSubscription?.stop) : null),
    [currentSubscription]
  );

  const showMessage = useMemo(() => {
    if (isTrial || !isActiveSubscription) return true;

    if (daysLeft !== null && daysLeft > 10) return false;

    if (currentSubscription?.billingStatus === BillingStatus.automatically_renewed) return false;

    return true;
  }, [isTrial, isActiveSubscription, daysLeft, currentSubscription]);

  const daysLeftMessage = useMemo(
    () => `${daysLeft} ${daysLeft === 1 ? messageCopy.day.singular : messageCopy.day.plural}`,
    [daysLeft]
  );

  const titleSubtitle = useMemo(() => {
    const message = {
      title: "",
      subtitle: "",
    };

    // active trial
    if (isActiveTrial) {
      if (daysLeft === 0) {
        message.title = messageCopy.trial.endsToday;

        return message;
      }

      message.title = messageCopy.trial.title;
      message.subtitle = daysLeftMessage;

      return message;
    }

    // expired subscription or trial
    if (!currentSubscription || !isActiveSubscription) {
      message.title = messageCopy.expired.title;
      message.subtitle = messageCopy.expired.subtitle;

      return message;
    }

    if (daysLeft === null) return message;

    // not automatically renewed subscription
    if (currentSubscription.billingStatus === BillingStatus.not_automatically_renewed) {
      if (daysLeft === 0) {
        message.title = messageCopy.expireSoon.endsToday;

        return message;
      }

      message.title = messageCopy.expireSoon.title;
      message.subtitle = daysLeftMessage;
    }

    return message;
  }, [daysLeft, isActiveTrial, currentSubscription, isActiveSubscription, daysLeftMessage]);

  const renderMessage = useCallback(() => {
    const { title, subtitle } = titleSubtitle;

    return (
      <BoldText>
        {title} {subtitle && <Badge status={IStatus.error}>{subtitle}</Badge>}
      </BoldText>
    );
  }, [titleSubtitle]);

  if (!showMessage) return null;

  return (
    <Message
      status={IStatus.error}
      showIcon={false}
      justifyContent={isCenter ? "center" : undefined}
      dataTestid={`${isTrial ? "trial" : "expired"}_subscription_message`}
    >
      <Spacer size="s" spaceDirection="horizontal" align="center">
        {renderMessage()}
        {bandPermissions.MANAGE_SUBSCRIPTIONS && (
          <Button onClick={() => pushRoute(BAND_PATHS.SUBSCRIPTION_OVERVIEW)} size="s">
            {messageCopy.button}
          </Button>
        )}
      </Spacer>
    </Message>
  );
};
